import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    Calendar as CalendarIcon,
    Users,
    Target,
    TrendingUp,
    Download,
    Mail,
    Activity,
    TrendingDown,
    RefreshCw
} from 'lucide-react';
import LoadingSpinner from '@/components/LoadingComponent';
import { Progress } from '@/components/ui/progress';
import NewsletterOverview from '@/lib/Newsletters/Overview';
import NewsletterDashboard from './NewsletterDashboard';

interface NewsletterStats {
    name: string
    active: number
}

interface NewsletterGoal {
    newsletterName: string;
    quarterGoal: number;
    initialValue: number;
}

interface SubscriptionAnalyticsData {
    dailyStats: {
        [key: string]: Array<{
            date: string;
            active: number;
            pending: number;
            inactive: number;
            other: number;
            total: number;
        }>;
    };
    acquisitionSources: {
        [key: string]: Array<{
            source: string;
            subscribers: number;
        }>;
    };
    totals: {
        [key: string]: {
            active: number;
            pending: number;
            inactive: number;
            other: number;
            total: number;
        };
    };
    period: {
        startDate: string;
        endDate: string;
    };
}

interface MetricsResponse {
    metrics: Array<{ name: string; active: number }>;
    period: {
        startDate: string;
        endDate: string;
    };
    goals: Record<string, NewsletterGoal>;
    subscriptionAnalytics?: SubscriptionAnalyticsData;
    yesterdayIncrease?: number;
    yesterdayTotal?: number;
    currentTotal?: number;
    summary?: any;
}

type DateRange = 'today' | 'last_7d' | 'last_30d' | 'last_90d' | 'last_180d' | 'last_365d' | 'custom';

const CADERNOS = [
    { value: 'todos', label: 'Todos os Cadernos', icon: '/logo.png' },
    { value: 'thenews', label: 'the news', icon: '/thenews.webp' },
    { value: 'thechamps', label: 'the champs', icon: '/thechamps.webp' },
    { value: 'thestories', label: 'the stories', icon: '/thestories.webp' },
    { value: 'goget', label: 'GoGet', icon: '/goget.webp' },
    { value: 'rising', label: 'rising', icon: '/rising.webp' },
    { value: 'thebizness', label: 'the bizness', icon: '/thebizness.webp' }
] as const;

interface NewsletterInsightsProps {
    initialCaderno?: typeof CADERNOS[number]['value'];
}

const NewsletterInsights: React.FC<NewsletterInsightsProps> = ({ initialCaderno = 'todos' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [analyticsData, setAnalyticsData] = useState<any>(null);
    const [goalsData, setGoalsData] = useState<any>(null);
    const [metricsData, setMetricsData] = useState<MetricsResponse | null>(null);
    const [selectedCaderno, setSelectedCaderno] = useState<typeof CADERNOS[number]['value']>(initialCaderno);
    const [selectedTab, setSelectedTab] = useState<'analysis' | 'goals'>('analysis');
    const [dateRange, setDateRange] = useState<{
        from: Date;
        to: Date;
    }>({
        from: startOfMonth(new Date()),
        to: new Date()
    });
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [stats, setStats] = useState<NewsletterStats[]>([])
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange>('last_7d');

    const newsletters = [
        "thechamps",
        "thenews",
        "thebizness",
        "rising",
        "goget",
        "healthtimes",
        "thestories",
        "thejobs",
        "dollarbill",
        "trendreport"
    ]

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || '{}')
                const promises = newsletters.map(async (newsletter) => {
                    const response = await fetch(
                        `https://waffle-v2.thenewcc.workers.dev/newsletters/subscribers/${newsletter}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${user.token}`
                            }
                        }
                    )
                    const data = await response.json()
                    return { name: newsletter, active: data.active }
                })
                const results = await Promise.all(promises)
                setStats(results)
            } catch (error) {
                console.error("Error fetching newsletter stats:", error)
            }
        }

        fetchStats()
    }, [])

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        setAnalyticsData(null);
        setGoalsData(null);
    
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
    
            // Buscar métricas gerais e dados de subscription analytics
            const [metricsResponse, subscriptionAnalyticsResponse, summaryResponse] = await Promise.all([
                fetch(
                    'https://waffle-v2.thenewcc.workers.dev/newsletters/newsletter-metrics',
                    {
                        headers: { 'Authorization': `Bearer ${user.token}` }
                    }
                ),
                fetch(
                    `https://waffle-v2.thenewcc.workers.dev/newsletters/subscription-analytics/${selectedDateRange}`,
                    {
                        headers: { 'Authorization': `Bearer ${user.token}` }
                    }
                ),
                fetch(
                    'https://waffle-v2.thenewcc.workers.dev/newsletters/summary',
                    {
                        headers: { 'Authorization': `Bearer ${user.token}` }
                    }
                )
            ]);

            if (metricsResponse.status === 401 || metricsResponse.status === 403 || subscriptionAnalyticsResponse.status === 401 || subscriptionAnalyticsResponse.status === 403 || summaryResponse.status === 401 || summaryResponse.status === 403) {
                localStorage.removeItem('user');
                window.location.reload();
                throw new Error('Sessão expirada. Por favor, faça login novamente.');
              }
    
            if (!metricsResponse.ok || !subscriptionAnalyticsResponse.ok) {
                throw new Error('Falha ao buscar dados');
            }
    
            const [metricsResult, subscriptionAnalyticsResult, summaryResult] = await Promise.all([
                metricsResponse.json(),
                subscriptionAnalyticsResponse.json(),
                summaryResponse.json()
            ]);

            setMetricsData({
                ...metricsResult,
                subscriptionAnalytics: subscriptionAnalyticsResult,
                summary: summaryResult
            });
    
            // Processar dados das metas
            if (selectedCaderno === 'todos') {
                setGoalsData(metricsResult.goals);
            } else {
                const [analyticsResponse, goalsResponse] = await Promise.all([
                    fetch(
                        `https://waffle-v2.thenewcc.workers.dev/newsletters/analytics/${selectedCaderno}/` +
                        `${format(dateRange.from, 'yyyy-MM-dd')}/${format(dateRange.to, 'yyyy-MM-dd')}`,
                        {
                            headers: { 'Authorization': `Bearer ${user.token}` }
                        }
                    ),
                    fetch(
                        `https://waffle-v2.thenewcc.workers.dev/newsletters/quarterly-goals/${selectedCaderno}`,
                        {
                            headers: { 'Authorization': `Bearer ${user.token}` }
                        }
                    )
                ]);

                if (!analyticsResponse.ok || !goalsResponse.ok) {
                    const errorText = `Failed to fetch data: ${analyticsResponse.status} ${goalsResponse.status}`;
                    throw new Error(errorText);
                }

                const [analyticsResult, goalsResult] = await Promise.all([
                    analyticsResponse.json(),
                    goalsResponse.json()
                ]);

                setAnalyticsData(analyticsResult);
                setGoalsData(goalsResult);
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setError('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            fetchData();
        }
    }, [selectedCaderno, dateRange, selectedDateRange]);

    const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);
    const selectedNewsletter = CADERNOS.find(c => c.value === selectedCaderno);

    const calculateTimeProgress = () => {
        if (!goalsData || !goalsData.period || !goalsData.period.startDate || !goalsData.period.endDate) return 0;
        const startDate = new Date(goalsData.period.startDate);
        const endDate = new Date(goalsData.period.endDate);
        const today = new Date();

        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const passedDays = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

        return Math.min(100, Math.max(0, (passedDays / totalDays) * 100));
    };

    const handleExportData = () => {
        const data = selectedTab === 'analysis' ? analyticsData : goalsData;
        if (!data) return;

        const csvData = [
            ['Date', 'Active', 'Inactive', 'Total', 'Growth'].join(','),
            ...data.daily.map((day: any) => [
                day.date,
                day.active,
                day.inactive,
                day.total_active,
                day.active - day.inactive
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedCaderno}_${selectedTab}_${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleDateRangeChange = (value: DateRange) => {
        setSelectedDateRange(value);
        if (value !== 'custom') {
            setIsCalendarOpen(false);
        }
    };

    if (loading) return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                    <img
                        src={selectedNewsletter?.icon}
                        alt={selectedNewsletter?.label}
                        className="h-8 w-auto"
                    />
                    <div>
                        <h2 className="text-xl font-bold">{selectedNewsletter?.label}</h2>
                        <p className="text-sm text-gray-500">
                            {selectedTab === 'analysis' ? 'Análise de Desempenho' : 'Acompanhamento de Metas'}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <Select value={selectedCaderno} onValueChange={(value: "todos" | "thenews" | "thechamps" | "thestories" | "goget" | "rising" | "thebizness") => setSelectedCaderno(value)}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Selecione o caderno" />
                        </SelectTrigger>
                        <SelectContent>
                            {CADERNOS.map((caderno) => (
                                <SelectItem key={caderno.value} value={caderno.value}>
                                    {caderno.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={selectedDateRange} onValueChange={handleDateRangeChange}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Selecione o período">
                                {selectedDateRange === 'custom' 
                                    ? `${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`
                                    : {
                                        'today': 'Hoje',
                                        'yesterday': 'Ontem',
                                        'last_7d': 'Últimos 7 dias',
                                        'last_14d': 'Últimos 14 dias',
                                        'last_30d': 'Últimos 30 dias',
                                        'last_90d': 'Últimos 90 dias',
                                        'last_180d': 'Últimos 180 dias',
                                        'last_365d': 'Últimos 365 dias',
                                        'this_week': 'Esta semana',
                                        'last_week': 'Semana passada',
                                        'this_month': 'Este mês',
                                        'last_month': 'Mês passado',
                                        'custom': 'Personalizado'
                                    }[selectedDateRange]
                                }
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Hoje</SelectItem>
                            <SelectItem value="yesterday">Ontem</SelectItem>
                            <SelectItem value="last_7d">Últimos 7 dias</SelectItem>
                            <SelectItem value="last_14d">Últimos 14 dias</SelectItem>
                            <SelectItem value="last_30d">Últimos 30 dias</SelectItem>
                            <SelectItem value="this_week">Esta semana</SelectItem>
                            <SelectItem value="last_week">Semana passada</SelectItem>
                            <SelectItem value="this_month">Este mês</SelectItem>
                            <SelectItem value="last_month">Mês passado</SelectItem>
                            <SelectItem value="custom">Personalizado</SelectItem>
                        </SelectContent>
                    </Select>

                    {selectedDateRange === 'custom' && (
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-[280px]">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateRange.from && dateRange.to ? (
                                        <>
                                            {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                                            {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                                        </>
                                    ) : (
                                        <span>Selecione o período</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    initialFocus
                                    mode="range"
                                    defaultMonth={dateRange.from}
                                    selected={{ from: dateRange.from, to: dateRange.to }}
                                    onSelect={(range) => {
                                        if (range?.from && range?.to) {
                                            setDateRange({ from: range.from, to: range.to });
                                            setIsCalendarOpen(false);
                                        }
                                    }}
                                    numberOfMonths={2}
                                    locale={ptBR}
                                />
                            </PopoverContent>
                        </Popover>
                    )}

                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setSelectedTab(tab => tab === 'analysis' ? 'goals' : 'analysis')}>
                            {selectedTab === 'analysis' ? 'Ver Metas' : 'Ver Análise'}
                        </Button>
                        <Button variant="outline" onClick={handleExportData}>
                            <Download className="mr-2 h-4 w-4" />
                            Exportar
                        </Button>
                        <Button variant="outline" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center h-[400px]">
                <LoadingSpinner />
            </div>
        </div>
    );
    if (error) return <div className="text-red-500">{error}</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                    <img
                        src={selectedNewsletter?.icon}
                        alt={selectedNewsletter?.label}
                        className="h-8 w-auto"
                    />
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{selectedNewsletter?.label}</h2>
                        <p className="text-sm text-gray-500">
                            {selectedTab === 'analysis' ? 'Análise de Desempenho' : 'Acompanhamento de Metas'}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <Select value={selectedCaderno} onValueChange={(value: "todos" | "thenews" | "thechamps" | "thestories" | "goget" | "rising" | "thebizness") => setSelectedCaderno(value)}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Selecione o caderno" />
                        </SelectTrigger>
                        <SelectContent>
                            {CADERNOS.map((caderno) => (
                                <SelectItem key={caderno.value} value={caderno.value}>
                                    {caderno.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={selectedDateRange} onValueChange={handleDateRangeChange}>
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Selecione o período">
                                {selectedDateRange === 'custom' 
                                    ? `${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`
                                    : {
                                        'today': 'Hoje',
                                        'yesterday': 'Ontem',
                                        'last_7d': 'Últimos 7 dias',
                                        'last_14d': 'Últimos 14 dias',
                                        'last_30d': 'Últimos 30 dias',
                                        'last_90d': 'Últimos 90 dias',
                                        'last_180d': 'Últimos 180 dias',
                                        'last_365d': 'Últimos 365 dias',
                                        'this_week': 'Esta semana',
                                        'last_week': 'Semana passada',
                                        'this_month': 'Este mês',
                                        'last_month': 'Mês passado',
                                        'custom': 'Personalizado'
                                    }[selectedDateRange]
                                }
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Hoje</SelectItem>
                            <SelectItem value="yesterday">Ontem</SelectItem>
                            <SelectItem value="last_7d">Últimos 7 dias</SelectItem>
                            <SelectItem value="last_14d">Últimos 14 dias</SelectItem>
                            <SelectItem value="last_30d">Últimos 30 dias</SelectItem>
                            <SelectItem value="last_90d">Últimos 90 dias</SelectItem>
                            <SelectItem value="last_180d">Últimos 180 dias</SelectItem>
                            <SelectItem value="last_365d">Últimos 365 dias</SelectItem>
                            <SelectItem value="this_week">Esta semana</SelectItem>
                            <SelectItem value="last_week">Semana passada</SelectItem>
                            <SelectItem value="this_month">Este mês</SelectItem>
                            <SelectItem value="last_month">Mês passado</SelectItem>
                            <SelectItem value="custom">Personalizado</SelectItem>
                        </SelectContent>
                    </Select>

                    {selectedDateRange === 'custom' && (
                        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-[280px]">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateRange.from && dateRange.to ? (
                                        <>
                                            {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                                            {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                                        </>
                                    ) : (
                                        <span>Selecione o período</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    initialFocus
                                    mode="range"
                                    defaultMonth={dateRange.from}
                                    selected={{ from: dateRange.from, to: dateRange.to }}
                                    onSelect={(range) => {
                                        if (range?.from && range?.to) {
                                            setDateRange({ from: range.from, to: range.to });
                                            setIsCalendarOpen(false);
                                        }
                                    }}
                                    numberOfMonths={2}
                                    locale={ptBR}
                                />
                            </PopoverContent>
                        </Popover>
                    )}

                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setSelectedTab(tab => tab === 'analysis' ? 'goals' : 'analysis')}>
                            {selectedTab === 'analysis' ? 'Ver Metas' : 'Ver Análise'}
                        </Button>
                        <Button variant="outline" onClick={handleExportData}>
                            <Download className="mr-2 h-4 w-4" />
                            Exportar
                        </Button>
                        <Button variant="outline" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {selectedTab === 'analysis' ? (
                selectedCaderno === 'todos' ? (
                    metricsData ? (
                        <NewsletterOverview
                            metrics={metricsData.metrics}
                            period={metricsData.period}
                            goals={metricsData.goals}
                            stats={stats}
                            summary={metricsData.summary}
                        />
                    ) : <LoadingSpinner />
                ) : (
                    analyticsData ? (
                        <>
                            <div className="grid gap-4 md:grid-cols-4">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Total de Assinantes</CardTitle>
                                        <Users className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatNumber(stats.find(stat => stat.name === selectedCaderno)?.active ?? 0)}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <span>vs. ontem</span>
                                            {((stats.find(stat => stat.name === selectedCaderno)?.active ?? 0) > (goalsData?.metrics?.yesterdayTotal ?? 0)) ? (
                                                <TrendingUp className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <TrendingDown className="h-3 w-3 text-red-500" />
                                            )}
                                            <span>{formatNumber(Math.abs((goalsData?.metrics?.yesterdayTotal ?? 0)))}</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Crescimento</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatNumber((goalsData?.metrics?.increase || 0))}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <span>vs. ontem</span>
                                            {(goalsData?.metrics?.increase ?? 0) > (goalsData?.metrics?.yesterdayIncrease ?? 0) ? (
                                                <TrendingUp className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <TrendingDown className="h-3 w-3 text-red-500" />
                                            )}
                                            <span>{formatNumber(Math.abs((goalsData?.metrics?.increase ?? 0) - (goalsData?.metrics?.yesterdayIncrease ?? 0)))}</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Taxa de Retenção</CardTitle>
                                        <Activity className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {(((analyticsData?.metrics?.active_subscribers || 0) / (analyticsData?.metrics?.total_subscribers || 1)) * 100)?.toFixed(1) ?? 0}%
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <span>vs. anterior</span>
                                            {analyticsData?.metrics?.retention_change > 0 ? (
                                                <TrendingUp className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <TrendingDown className="h-3 w-3 text-red-500" />
                                            )}
                                            <span>{Math.abs(analyticsData?.metrics?.retention_change || 0).toFixed(1)}%</span>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Taxa de Churn</CardTitle>
                                        <Mail className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {(((analyticsData?.metrics?.inactive_subscribers || 0) / (analyticsData?.metrics?.total_subscribers || 1)) * 100)?.toFixed(1) ?? 0}%
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {formatNumber(analyticsData?.metrics?.inactive_subscribers || 0)} cancelamentos
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>

                            <div className="grid gap-4 md:grid-cols-2">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Distribuição por Hora</CardTitle>
                                        <CardDescription>Horários com maior engajamento</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-[300px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={analyticsData?.hourlyDistribution || []}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="hour" tickFormatter={(hour) => `${hour}h`} />
                                                    <YAxis />
                                                    <Tooltip
                                                        formatter={(value) => [formatNumber(Number(value)), 'Inscritos']}
                                                        labelFormatter={(hour) => `${hour}:00`}
                                                    />
                                                    <Bar dataKey="count" name="Inscritos" fill="#FF3366" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>Distribuição Semanal</CardTitle>
                                        <CardDescription>Performance por dia da semana</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-[300px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={analyticsData?.weeklyDistribution || []}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="weekday" />
                                                    <YAxis />
                                                    <Tooltip formatter={(value) => [formatNumber(Number(value)), 'Inscritos']} />
                                                    <Bar dataKey="active" name="Inscritos" fill="#FF3366" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </>
                    ) : <LoadingSpinner />
                )
            ) : (
                selectedCaderno === 'todos' ? (
                    metricsData ? (
                        <NewsletterOverview
                            metrics={metricsData.metrics}
                            period={metricsData.period}
                            goals={metricsData.goals}
                            stats={stats}
                            summary={metricsData.summary}
                        />
                    ) : <LoadingSpinner />
                ) : (
                    goalsData ? (
                        <>
                            <Card className="bg-gradient-to-br from-[#FF3366] to-[#FF6B98] text-white">
                                <CardHeader>
                                    <CardTitle className="text-2xl">Meta de Crescimento</CardTitle>
                                    <CardDescription className="text-white/80">
                                        {selectedNewsletter?.label} - Meta: {formatNumber(goalsData?.metrics?.goal || 0)} novos inscritos
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-6">
                                        <div className="flex justify-between items-end">
                                            <div>
                                                <span className="text-4xl font-bold">{formatNumber(goalsData?.metrics?.increase || 0)}</span>
                                                <span className="text-white/80 ml-2">novos inscritos</span>
                                            </div>
                                            <span className="text-white/80">
                                                {goalsData?.metrics?.remainingDays || 0} dias restantes
                                            </span>
                                        </div>
                                        <div className="space-y-4">
                                            <Progress
                                                value={goalsData?.metrics?.goalProgress || 0}
                                                timeProgress={calculateTimeProgress()}
                                                className="h-3"
                                            />
                                            <div className="flex justify-between text-sm text-white/80">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-2 w-2 rounded-full bg-[#FFCE04]" />
                                                        <span>Meta: {(goalsData?.metrics?.goalProgress || 0).toFixed(1)}% concluído</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <div className="h-2 w-2 rounded-full bg-white" />
                                                        <span>Tempo: {calculateTimeProgress().toFixed(1)}% decorrido</span>
                                                    </div>
                                                </div>
                                                <span>
                                                    Faltam {formatNumber(goalsData?.metrics?.goal - (goalsData?.metrics?.increase || 0) || 0)} inscritos
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="grid gap-4 md:grid-cols-3">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Base Atual</CardTitle>
                                        <Users className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatNumber(stats.find(stat => stat.name === selectedCaderno)?.active || 0)}
                                        </div>
                                        <p className="text-xs text-muted-foreground">Total atual de inscritos</p>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Crescimento</CardTitle>
                                        <TrendingUp className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatNumber((goalsData?.metrics?.increase || 0))}
                                        </div>
                                        <p className="text-xs text-muted-foreground">Este semestre</p>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Média Necessária</CardTitle>
                                        <Target className="h-4 w-4 text-[#FF3366]" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">
                                            {formatNumber(Math.ceil((goalsData?.metrics?.goal - (goalsData?.metrics?.increase || 0)) /
                                                Math.max(1, goalsData?.metrics?.remainingDays || 0) || 0))}
                                        </div>
                                        <p className="text-xs text-muted-foreground">Inscritos/dia para meta</p>
                                    </CardContent>
                                </Card>
                            </div>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Projeção de Crescimento</CardTitle>
                                    <CardDescription>Baseada na média atual vs. média necessária</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[400px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={(() => {
                                                const daysLeft = goalsData?.metrics?.remainingDays || 0;
                                                const currentDaily = (goalsData?.metrics?.increase || 0) / Math.max(1, daysLeft);
                                                const requiredDaily = (goalsData?.metrics?.goal || 0 - (goalsData?.metrics?.increase || 0)) / Math.max(1, daysLeft);

                                                return Array.from({ length: daysLeft + 1 }, (_, index) => ({
                                                    day: index,
                                                    currentProjection: (goalsData?.metrics?.currentTotal || 0) + (currentDaily * index),
                                                    requiredProjection: (goalsData?.metrics?.currentTotal || 0) + (requiredDaily * index),
                                                    goal: (goalsData?.metrics?.currentTotal || 0) + (goalsData?.metrics?.goal || 0)
                                                }));
                                            })()}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="day" tickFormatter={(day) => `D+${day}`} />
                                                <YAxis />
                                                <Tooltip
                                                    formatter={(value: number) => [formatNumber(value), 'Inscritos']}
                                                    labelFormatter={(day) => `Dia ${day}`}
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="currentProjection"
                                                    name="Projeção Atual"
                                                    stroke="#FF3366"
                                                    strokeWidth={2}
                                                    dot={false}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="requiredProjection"
                                                    name="Meta Necessária"
                                                    stroke="#36D399"
                                                    strokeWidth={2}
                                                    dot={false}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="goal"
                                                    name="Meta Total"
                                                    stroke="#F87272"
                                                    strokeWidth={2}
                                                    strokeDasharray="5 5"
                                                    dot={false}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    ) : <LoadingSpinner />
                )
            )}

            
            <NewsletterDashboard newsletter={selectedCaderno} />
        </div>
    );
};

export default NewsletterInsights;